import { useEffect, useState } from 'react';

import {
    ErrorApi,
    IDIOMAS,
    NOMBRE_IDIOMA,
    type DatosLegales,
    type Idioma,
    type Invitacion,
    type T,
} from '@haus/core';

import { Boton } from '../componentes/Boton.js';
import { EntradaPin } from '../componentes/Pin.js';
import { estilosHoja } from '../componentes/Hoja.js';
import { Aviso, BarraAccion, Cabecera, Contenido, Pantalla, Tarjeta } from '../componentes/estructura.js';
import { useApp } from '../estado/app.js';
import css from './comun.module.css';

/**
 * 00 · Alta: quién eres, y este móvil como credencial.
 *
 * Ocurre una sola vez, al abrir el enlace de invitación. Es la pantalla que
 * convierte un móvil cualquiera en **la credencial**: lo que se guarda al
 * terminar no es una contraseña, es un token largo que el servidor solo
 * entrega aquí y del que solo guarda el hash.
 *
 * Hay DOS caminos, y se decide preguntando al servidor por la invitación antes
 * de pintar nada:
 *
 * - **Abierta** (lo normal). Oficina manda un enlace sin nombre. Aquí se pide
 *   el nombre y los datos, y con ellos se crea la ficha. Antes el nombre lo
 *   ponía oficina al generar la invitación, así que la persona empezaba con un
 *   nombre elegido por otro y sin forma de corregirlo.
 * - **Dirigida**. Oficina ya sabe a quién invita —un segundo móvil, un
 *   reemplazo— y el nombre ya existe: solo se pide el PIN.
 *
 * El PIN se elige aquí porque el servidor lo exige al canjear: sin PIN no hay
 * alta. Se pide dos veces, como cualquier cosa que no se puede recuperar.
 */
type Paso = 'intro' | 'datos' | 'pin' | 'repetir';

export function PantallaAlta({ token }: { token: string | null }) {
    const { t, idioma, cliente, guardar, ir, capacidades } = useApp();

    // Qué móvil es. Se calcula una vez y lo usan los dos sitios: la ficha que
    // se guarda del dispositivo y el bloque que ve la persona al vincularlo.
    const plataforma = capacidades.esIos
        ? 'ios'
        : /Android/.test(navigator.userAgent)
          ? 'android'
          : 'otro';
    const [inv, setInv] = useState<Invitacion | null>(null);
    // No es lo mismo «pregunte y la invitacion no vale» que «no pude
    // preguntar». Antes se trataban igual: sin red se inventaba una invitacion
    // valida y ABIERTA, asi que a una persona con invitacion dirigida se le
    // pedia el nombre que ya tenia puesto, y al canjear --que es lo primero
    // que necesita el servidor de verdad-- fallaba. Esta es la UNICA pantalla
    // de la app que no puede funcionar sin red: el token se canjea contra el
    // servidor o no hay alta.
    const [sinRed, setSinRed] = useState(false);
    const [paso, setPaso] = useState<Paso>('intro');
    const [primerPin, setPrimerPin] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [enviando, setEnviando] = useState(false);

    // Sube al pulsar «Volver a probar» y vuelve a lanzar el efecto.
    const [intento, setIntento] = useState(0);

    const [nombre, setNombre] = useState('');
    const [telefono, setTelefono] = useState('');
    const [correo, setCorreo] = useState('');
    const [idiomaElegido, setIdiomaElegido] = useState<Idioma>(idioma);

    // Se pregunta por la invitación antes de pintar el formulario. Es una
    // consulta que no la gasta y no da acceso a nada: solo dice si el enlace
    // sirve y si hay que pedir los datos de la persona.
    useEffect(() => {
        if (token === null) {
            return;
        }
        let vivo = true;
        setSinRed(false);
        void cliente
            .mirarInvitacion(token)
            .then((i) => {
                if (vivo) {
                    setInv(i);
                }
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                // Sin red no se puede saber, y **no se inventa**. Decirlo aquí
                // ahorra escribir el nombre y teclear el PIN dos veces para
                // acabar en un error que no explica nada.
                if (e instanceof ErrorApi && (e.codigo === 'sin_red' || e.reintentable)) {
                    setSinRed(true);

                    return;
                }
                // Un 4xx que no sabemos leer sí deja seguir: el canje dirá la
                // verdad, y es el servidor el que manda sobre el token.
                // `legal: null` y no un objeto vacío: no se ha podido leer,
                // que no es lo mismo que «no hay nada configurado». Con un
                // objeto vacío la pantalla diría «esta instalación todavía no
                // tiene configurados los datos del responsable» sobre una que
                // los tiene. Aquí, sencillamente, no se pinta el aviso.
                setInv({
                    valida: true,
                    abierta: true,
                    nombre: null,
                    expira_at: null,
                    motivo: null,
                    legal: null,
                });
            });

        return () => {
            vivo = false;
        };
    }, [token, cliente, intento]);

    if (token === null) {
        return (
            <Pantalla>
                <Cabecera titulo={t('alta.sin_token')} />
                <Contenido>
                    <p className={css.parrafo}>{t('alta.sin_token_ayuda')}</p>
                </Contenido>
            </Pantalla>
        );
    }

    if (sinRed) {
        return (
            <Pantalla>
                <Cabecera titulo={t('alta.sin_red')} />
                <Contenido>
                    <p className={css.parrafo}>{t('alta.sin_red_ayuda')}</p>
                </Contenido>
                <BarraAccion>
                    <Boton onClick={() => setIntento((n) => n + 1)}>{t('alta.reintentar')}</Boton>
                </BarraAccion>
            </Pantalla>
        );
    }

    if (inv !== null && !inv.valida) {
        const titulo =
            inv.motivo === 'invitacion_caducada'
                ? t('alta.error_caducada')
                : inv.motivo === 'invitacion_ya_usada'
                  ? t('alta.error_usada')
                  : t('alta.sin_token');
        const ayuda =
            inv.motivo === 'invitacion_caducada'
                ? t('alta.error_caducada_ayuda')
                : inv.motivo === 'invitacion_ya_usada'
                  ? t('alta.error_usada_ayuda')
                  : t('alta.sin_token_ayuda');

        return (
            <Pantalla>
                <Cabecera titulo={titulo} />
                <Contenido>
                    <p className={css.parrafo}>{ayuda}</p>
                </Contenido>
            </Pantalla>
        );
    }

    const abierta = inv?.abierta ?? true;

    async function canjear(pin: string): Promise<void> {
        setEnviando(true);
        setError(null);

        try {
            const alta = await cliente.canjearInvitacion({
                invite_token: token as string,
                pin,
                // Solo si la invitación es abierta. En una dirigida el servidor
                // los ignora, pero mandarlos sería mentir sobre la intención.
                ...(abierta
                    ? {
                          nombre: nombre.trim(),
                          idioma: idiomaElegido,
                          ...(telefono.trim() !== '' ? { telefono: telefono.trim() } : {}),
                          ...(correo.trim() !== '' ? { email: correo.trim() } : {}),
                      }
                    : {}),
                etiqueta: t('alta.dispositivo'),
                modelo: navigator.userAgent.slice(0, 120),
                navegador: navigator.vendor !== '' ? navigator.vendor : 'navegador',
                plataforma,
                app_version: __VERSION__,
            });

            await guardar({
                device_token: alta.device_token,
                device_id: alta.device_id,
                jwt: alta.jwt,
                expira_at: Date.now() + alta.expira_en * 1000,
                empleada: alta.employee,
                etiqueta_dispositivo: t('alta.dispositivo'),
            });

            ir({ n: 'hoy' });
        } catch (e) {
            setPrimerPin('');
            if (e instanceof ErrorApi) {
                const mapa: Record<string, string> = {
                    // `sin_red` es lo PRIMERO de la lista porque es lo que más
                    // pasa y lo que peor se contaba: `ErrorDeRed` hereda de
                    // `ErrorApi`, así que entra por esta rama y el `else` de
                    // abajo con el texto de «sin conexión» era código muerto.
                    // Resultado: una petición que no salió del móvil decía
                    // «Algo ha ido mal», que se lee como «la app está rota».
                    sin_red: t('error.sin_red'),
                    invitacion_caducada: t('alta.error_caducada'),
                    invitacion_ya_usada: t('alta.error_usada'),
                    invitacion_no_valida: t('alta.sin_token'),
                    pin_debil: t('pin.debil_iguales'),
                    pin_formato: t('pin.debil_iguales'),
                    falta_nombre: t('alta.falta_nombre'),
                    email_invalido: t('alta.error_email'),
                    email_repetido: t('alta.error_email_repetido'),
                    // El PIN de quien ya tenía uno: es un segundo móvil, no un
                    // alta. Sin esto también salía «Algo ha ido mal».
                    pin_incorrecto: t('alta.error_pin_existente'),
                    empleada_inactiva: t('error.dispositivo_revocado'),
                    // Con la ayuda dentro: aquí ya no hay nada que reintentar,
                    // así que el mensaje tiene que decir a quién acudir.
                    invitacion_bloqueada: `${t('alta.error_bloqueada')} · ${t('alta.error_bloqueada_ayuda')}`,
                };
                const restantes = e.extra['intentos_restantes'];
                setError(
                    e.codigo === 'pin_incorrecto' && typeof restantes === 'number'
                        ? `${t('alta.error_pin_existente')} · ${t('pin.error_intentos', { intentos: restantes })}`
                        : (mapa[e.codigo] ?? `${t('error.inesperado')} · ${e.codigo}`)
                );
                // Un problema con los datos se corrige en su pantalla, no
                // volviendo a teclear el PIN.
                setPaso(
                    ['falta_nombre', 'email_invalido', 'email_repetido'].includes(e.codigo)
                        ? 'datos'
                        : 'pin'
                );
            } else {
                // Aquí solo llega lo que NO es un error de la API: un fallo
                // nuestro. Decir «sin conexión» lo tapaba.
                setError(t('error.inesperado'));
                setPaso('pin');
            }
        } finally {
            setEnviando(false);
        }
    }

    // --- Los datos de la persona ----------------------------------------

    if (paso === 'datos') {
        const sinNombre = nombre.trim() === '';

        return (
            <Pantalla>
                <Cabecera
                    titulo={t('alta.datos_titulo')}
                    alVolver={() => {
                        setError(null);
                        setPaso('intro');
                    }}
                />
                <Contenido>
                    <p className={css.parrafo}>{t('alta.datos_intro')}</p>

                    <Campo etiqueta={t('alta.campo_nombre')}>
                        <input
                            className={`${estilosHoja.campo} ${estilosHoja.campoLinea}`}
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            placeholder={t('alta.campo_nombre_ph')}
                            autoComplete="name"
                            autoCapitalize="words"
                            maxLength={80}
                            // El foco va aquí a propósito: es el único campo
                            // obligatorio y lo primero que hay que hacer en
                            // esta pantalla. En un móvil ahorra un toque.
                            autoFocus
                        />
                    </Campo>

                    <Campo etiqueta={`${t('alta.campo_telefono')} · ${t('alta.opcional')}`}>
                        <input
                            className={`${estilosHoja.campo} ${estilosHoja.campoLinea}`}
                            value={telefono}
                            onChange={(e) => setTelefono(e.target.value)}
                            type="tel"
                            autoComplete="tel"
                            maxLength={40}
                        />
                    </Campo>

                    <Campo etiqueta={`${t('alta.campo_email')} · ${t('alta.opcional')}`}>
                        <input
                            className={`${estilosHoja.campo} ${estilosHoja.campoLinea}`}
                            value={correo}
                            onChange={(e) => setCorreo(e.target.value)}
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            autoCapitalize="off"
                            maxLength={200}
                        />
                    </Campo>

                    <Grupo etiqueta={t('alta.campo_idioma')}>
                        <div className={estilosHoja.opciones}>
                            {IDIOMAS.map((i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className={`${estilosHoja.opcion} ${idiomaElegido === i ? estilosHoja.opcionActiva : ''}`}
                                    onClick={() => setIdiomaElegido(i)}
                                >
                                    {NOMBRE_IDIOMA[i]}
                                </button>
                            ))}
                        </div>
                    </Grupo>

                    {error !== null && <Aviso>{error}</Aviso>}
                </Contenido>

                <BarraAccion nota={sinNombre ? t('alta.falta_nombre') : undefined}>
                    <Boton
                        disabled={sinNombre}
                        onClick={() => {
                            setError(null);
                            setPaso('pin');
                        }}
                    >
                        {t('alta.continuar')}
                    </Boton>
                </BarraAccion>
            </Pantalla>
        );
    }

    // --- El PIN ----------------------------------------------------------

    if (paso === 'pin' || paso === 'repetir') {
        return (
            <Pantalla>
                <Cabecera
                    titulo={paso === 'pin' ? t('pin.crear_titulo') : t('pin.repetir_titulo')}
                    subtitulo={paso === 'pin' ? t('pin.crear_intro') : undefined}
                    alVolver={() => {
                        setError(null);
                        setPrimerPin('');
                        setPaso(paso === 'repetir' ? 'pin' : abierta ? 'datos' : 'intro');
                    }}
                />
                <Contenido>
                    <EntradaPin
                        error={error}
                        etiquetaBorrar={t('pin.borrar')}
                        onCompleto={(pin) => {
                            if (paso === 'pin') {
                                setPrimerPin(pin);
                                setError(null);
                                setPaso('repetir');

                                return;
                            }
                            if (pin !== primerPin) {
                                setError(t('pin.no_coincide'));
                                setPaso('pin');
                                setPrimerPin('');

                                return;
                            }
                            void canjear(pin);
                        }}
                    />
                    {enviando && <p className={css.parrafo}>{t('comun.cargando')}</p>}
                </Contenido>
            </Pantalla>
        );
    }

    // --- La portada ------------------------------------------------------

    return (
        <Pantalla>
            <Contenido>
                <span className="etiqueta-mono">{t('alta.etiqueta')}</span>
                <h1 className={css.titulo}>
                    {abierta || inv?.nombre === null || inv === null
                        ? t('alta.titulo')
                        : t('alta.hola', { empleada: inv.nombre })}
                </h1>
                <p className={css.parrafo}>{t('alta.intro')}</p>

                <Tarjeta>
                    <div className={css.pasos}>
                        <Paso
                            hecho
                            titulo={t('alta.paso_invitacion')}
                            sub={
                                abierta
                                    ? t('alta.paso_invitacion_sub_abierta')
                                    : t('alta.paso_invitacion_sub', { empleada: inv?.nombre ?? '' })
                            }
                        />
                        {abierta && (
                            <Paso numero="2" actual titulo={t('alta.paso_datos')} sub={t('alta.paso_datos_sub')} />
                        )}
                        <Paso
                            numero={abierta ? '3' : '2'}
                            actual={!abierta}
                            titulo={t('alta.paso_pin')}
                            sub={t('alta.paso_pin_sub')}
                        />
                        {/* AQUI IBA «Activa huella o Face ID · Opcional, para
                            no teclear el PIN», y no hay nada detras.

                            WebAuthn esta en el esquema (`device.webauthn_cred_id`)
                            y en el handoff, marcado como OPCIONAL, y no esta
                            implementado en ninguna parte: ni registro, ni
                            verificacion de la firma en el servidor, ni un boton.
                            O sea que esta pantalla --la PRIMERA que ve una
                            empleada-- prometia un paso que despues no aparece
                            nunca. Es el mismo fallo que el distintivo de HTTPS
                            cuando era una cadena fija: afirmar sin comprobar.

                            No se arregla poniendo el boton: verificar una
                            asercion de WebAuthn es CBOR, COSE y una firma ES256
                            del lado del servidor, y una credencial a medias es
                            peor que ninguna. Cuando se haga, esto vuelve --con
                            sus textos, que se quitaron del catalogo para que no
                            quede una clave suelta invitando a enchufarla a
                            nada--. */}
                        {/* Va el ULTIMO a proposito: es lo que tiene que hacer
                            justo despues de elegir el PIN, y es lo que nadie
                            deduce. `alta.intro` ya dice que el enlace solo vale
                            una vez, pero no decia que hacer en su lugar, asi
                            que lo normal era guardar el enlace y volver a
                            abrirlo --y entonces la app dice que no vale y
                            parece que se ha perdido el alta--. */}
                        <Paso
                            numero={abierta ? '4' : '3'}
                            titulo={t('alta.paso_instalar')}
                            sub={t(
                                plataforma === 'ios'
                                    ? 'alta.paso_instalar_ios'
                                    : plataforma === 'android'
                                      ? 'alta.paso_instalar_android'
                                      : 'alta.paso_instalar_otro'
                            )}
                        />
                    </div>
                </Tarjeta>

                <div className={css.bloqueMono}>
                    <span className="etiqueta-mono">{t('alta.dispositivo')}</span>
                    {/* Aquí ponía `navigator.vendor`, que en un iPhone dice
                        «Apple Computer, Inc.» y en Android «Google Inc.»: a
                        quien va a limpiar un piso eso no le dice nada, y menos
                        aún a quien le esté ayudando por teléfono. Lo que sirve
                        es qué móvil es, que además es lo mismo que se guarda en
                        la ficha del dispositivo. */}
                    <div className={css.filaMono}>
                        <span>{t('alta.dev_movil')}</span>
                        <span>{plataforma === 'ios' ? 'iPhone' : plataforma === 'android' ? 'Android' : 'web'}</span>
                    </div>
                    <div className={css.filaMono}>
                        <span>{t('alta.dev_video')}</span>
                        <span>{capacidades.formatoVideo ?? t('alta.dev_sin_video')}</span>
                    </div>
                    <div className={css.filaMono}>
                        <span>{t('alta.dev_segundo_plano')}</span>
                        <span>{t(capacidades.subidaEnSegundoPlano ? 'alta.dev_si' : 'alta.dev_no')}</span>
                    </div>
                </div>

                <p className={css.pasoSub}>{t('alta.nota_revocacion')}</p>

                {/*
                  * El aviso de proteccion de datos (0024).
                  *
                  * Aqui y no despues: esta es la pantalla en la que entrega su
                  * nombre, su telefono y su correo. Decirselo cuando ya los ha
                  * escrito es decirselo tarde.
                  *
                  * Y NO se pinta si no llego --`legal` a null--, porque un
                  * aviso que no se ha podido leer no es un aviso. Ver el
                  * camino de respaldo de arriba.
                  */}
                {inv?.legal != null && <AvisoDatos legal={inv.legal} t={t} />}

                {error !== null && <Aviso>{error}</Aviso>}
            </Contenido>

            <BarraAccion>
                <Boton onClick={() => setPaso(abierta ? 'datos' : 'pin')} disabled={enviando}>
                    {abierta ? t('alta.continuar') : t('alta.boton')}
                </Boton>
            </BarraAccion>
        </Pantalla>
    );
}

/** Etiqueta encima del campo. Nada de placeholder como única etiqueta: en
 *  cuanto se escribe desaparece y ya no se sabe qué pedía. */
function Campo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
    return (
        <label className={css.campoBloque}>
            <span className="etiqueta-mono">{etiqueta}</span>
            {children}
        </label>
    );
}

/**
 * Lo mismo, pero para un grupo de botones, y NO con `<label>`.
 *
 * Un `<label>` que envuelve varios controles le presta su texto a todos: el
 * botón «Español» acababa anunciándose como «Idioma de la app English», porque
 * el nombre accesible se calcula juntando el contenido de la etiqueta. Con un
 * grupo y `aria-label` cada botón se llama como lo que dice.
 */
function Grupo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
    return (
        <div className={css.campoBloque} role="group" aria-label={etiqueta}>
            <span className="etiqueta-mono">{etiqueta}</span>
            {children}
        </div>
    );
}

function Paso({
    numero,
    titulo,
    sub,
    hecho,
    actual,
}: {
    numero?: string;
    titulo: string;
    sub: string;
    hecho?: boolean;
    actual?: boolean;
}) {
    const clase = hecho === true ? css.pasoHecho : actual === true ? css.pasoActual : '';

    return (
        <div className={css.paso}>
            <span className={`${css.pasoCirculo} ${clase}`}>{hecho === true ? '✓' : numero}</span>
            <div>
                <div className={css.pasoTitulo}>{titulo}</div>
                <div className={css.pasoSub}>{sub}</div>
            </div>
        </div>
    );
}

/**
 * El aviso de protección de datos del alta (0024).
 *
 * Distinto del que ve el huésped, y tiene que serlo: a él se le habla de los
 * vídeos de su apartamento, y aquí a ella de SUS datos —el nombre que va en el
 * fichero de cada vídeo, el teléfono, el correo y el móvil que está
 * vinculando—.
 *
 * Y dice una cosa que no dice el del huésped: si al vencer el plazo se borra
 * también **quién** hizo la limpieza. Eso es `purga_modo`, existía desde 0007 y
 * no se lo enseñaba nadie a la persona a la que le afecta.
 */
function AvisoDatos({ legal, t }: { legal: DatosLegales; t: T }) {
    // El separador se pone con `join` sobre la lista ya filtrada, nunca escrito
    // entre los valores: sin NIF la línea empezaría por un punto colgando. Ya
    // mordió con el modelo del móvil —«· otro · último acceso nunca»—.
    const responsable = [legal.titular, legal.nif, legal.direccion].filter(
        (x): x is string => x !== null
    );

    return (
        <div className={css.bloqueMono}>
            <span className="etiqueta-mono">{t('alta.rgpd_titulo')}</span>
            <p className={css.pasoSub}>{t('alta.rgpd_que')}</p>
            <p className={css.pasoSub}>
                {legal.retencion_dias === 0
                    ? t('alta.rgpd_plazo_indefinido')
                    : t('alta.rgpd_plazo', { dias: legal.retencion_dias })}{' '}
                {legal.purga_modo === 'anonimizar' && t('alta.rgpd_anonimiza')}
            </p>

            {responsable.length > 0 && (
                <p className={css.pasoSub}>
                    {t('alta.rgpd_responsable')}: {responsable.join(' · ')}
                </p>
            )}

            {legal.contacto !== null && (
                <p className={css.pasoSub}>
                    {t('alta.rgpd_derechos', { contacto: legal.contacto })}
                </p>
            )}

            {(legal.titular === null || legal.contacto === null) && (
                <p className={css.pasoSub}>{t('alta.rgpd_sin_configurar')}</p>
            )}
        </div>
    );
}
